/* global VideoEncoder, AudioEncoder, VideoFrame, AudioData */

// services/webCodecsVideoEncoder.js - Client-Side WebM Video Encoder with Audio
// Uses WebCodecs API for browser-native VP9 + Opus encoding

/**
 * Check if WebCodecs is supported in current browser
 * @returns {boolean}
 */
export const isWebCodecsSupported = () => {
  return (
    typeof VideoEncoder !== 'undefined' &&
    typeof AudioEncoder !== 'undefined' &&
    typeof VideoFrame !== 'undefined' &&
    typeof AudioData !== 'undefined'
  );
};

/**
 * Main WebCodecs Video Encoder Class
 */
export class WebCodecsVideoEncoder {
  constructor(options) {
    this.slides = options.slides || [];
    this.slideTemplates = options.slideTemplates || {};
    this.slideImages = options.slideImages || [];
    this.renderSlideToCanvas = options.renderSlideToCanvas;
    this.getSlideImageUrl = options.getSlideImageUrl;
    this.audioFile = options.audioFile || null;
    this.transition = options.transition || 'none';
    this.fps = options.fps || 30;
    this.duration = options.duration || 3;
    this.onProgress = options.onProgress || (() => {});
    
    this.width = 1920;
    this.height = 1080;
    this.bitrate = 5_000_000;
    
    this.encoder = null;
    this.audioEncoder = null;
    this.muxer = null;
    this.frameCount = 0;
  }

  /**
   * Initialize VideoEncoder for VP9
   */
  async initEncoder() {
    const { Muxer, ArrayBufferTarget } = await import('webm-muxer');
    
    const target = new ArrayBufferTarget();
    
    const muxerConfig = {
      target,
      video: {
        codec: 'V_VP9',
        width: this.width,
        height: this.height,
      },
      fastStart: false,
    };

    if (this.audioFile) {
      muxerConfig.audio = {
        codec: 'A_OPUS',
        numberOfChannels: 2,
        sampleRate: 48000,
      };
    }

    this.muxer = new Muxer(muxerConfig);
    this.muxerTarget = target;

    this.encoder = new VideoEncoder({
      output: (chunk, metadata) => {
        this.muxer.addVideoChunk(chunk, metadata);
      },
      error: (error) => {
       
        throw error;
      },
    });

    this.encoder.configure({
      codec: 'vp09.00.10.08',
      width: this.width,
      height: this.height,
      bitrate: this.bitrate,
      framerate: this.fps,
    });

    
  }

  /**
   * Initialize AudioEncoder for Opus
   */
  async initAudioEncoder() {
    if (!this.audioFile) {
      return false;
    }

    this.audioEncoder = new AudioEncoder({
      output: (chunk, metadata) => {
        this.muxer.addAudioChunk(chunk, metadata);
      },
      error: (error) => {
        
        throw error;
      },
    });

    try {
      this.audioEncoder.configure({
        codec: 'opus',
        sampleRate: 48000,
        numberOfChannels: 2,
        bitrate: 128000,
      });

     
      return true;
    } catch (error) {
     
      this.audioFile = null;
      this.audioEncoder = null;
      return false;
    }
  }

  /**
   * Encode audio from file
   */
  async encodeAudio() {
    if (!this.audioFile || !this.audioEncoder) {
      return;
    }

    try {
      
      
      const audioContext = new AudioContext({ sampleRate: 48000 });
      const arrayBuffer = await this.audioFile.arrayBuffer();
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

      const sampleRate = 48000;
      const channels = 2;
      const frameSize = 960; // Opus frame size at 48kHz

      let timestamp = 0;
      
      for (let offset = 0; offset < audioBuffer.length; offset += frameSize) {
        const frameLength = Math.min(frameSize, audioBuffer.length - offset);
        const frameData = this.extractAudioFrame(audioBuffer, offset, frameLength, channels);
        
        const audioData = new AudioData({
          format: 'f32-planar',
          sampleRate: sampleRate,
          numberOfFrames: frameLength,
          numberOfChannels: channels,
          timestamp: timestamp,
          data: frameData,
        });

        this.audioEncoder.encode(audioData);
        audioData.close();

        timestamp += (frameLength / sampleRate) * 1_000_000;
      }

      await this.audioEncoder.flush();
      

      await audioContext.close();
    } catch (error) {
      
      this.audioFile = null;
    }
  }

  /**
   * Extract audio frame data
   */
  extractAudioFrame(audioBuffer, offset, length, channels) {
    const data = new Float32Array(length * channels);
    
    for (let channel = 0; channel < channels; channel++) {
      const sourceChannel = Math.min(channel, audioBuffer.numberOfChannels - 1);
      const channelData = audioBuffer.getChannelData(sourceChannel);
      
      for (let i = 0; i < length; i++) {
        const sample = offset + i < channelData.length ? channelData[offset + i] : 0;
        data[channel * length + i] = sample;
      }
    }
    
    return data;
  }

  /**
   * Render slide to canvas
   */
  async renderSlide(slideIndex) {
    const canvas = document.createElement('canvas');
    canvas.width = this.width;
    canvas.height = this.height;

    const slide = this.slides[slideIndex];
    const templateId = this.slideTemplates[slideIndex] || 'modern_bullets_right';
    const imageUrl = this.getSlideImageUrl(slideIndex);

    await this.renderSlideToCanvas(canvas, slide.extractedContent, templateId, imageUrl);
    return canvas;
  }

  /**
   * Encode frame
   */
  async encodeFrame(canvas, timestamp) {
    const frame = new VideoFrame(canvas, {
      timestamp: timestamp * 1_000_000,
    });

    if (this.encoder.encodeQueueSize > 30) {
      await new Promise(resolve => setTimeout(resolve, 10));
    }

    this.encoder.encode(frame, { keyFrame: this.frameCount % 150 === 0 });
    frame.close();

    this.frameCount++;
  }

  /**
   * Generate video frames
   */
  async generateVideoFrames() {
    
    
    let currentTime = 0;
    const startProgress = this.audioFile ? 40 : 15;
    const endProgress = 90;

    for (let i = 0; i < this.slides.length; i++) {
      const slide = this.slides[i];
      const slideDuration = (this.audioFile && slide.duration) ? slide.duration : this.duration;
      const canvas = await this.renderSlide(i);
      const frames = Math.ceil(slideDuration * this.fps);

      

      for (let f = 0; f < frames; f++) {
        await this.encodeFrame(canvas, currentTime + (f / this.fps));
      }

      currentTime += slideDuration;
      this.onProgress(Math.round(startProgress + ((i + 1) / this.slides.length) * (endProgress - startProgress)));
    }

    await this.encoder.flush();
    
  }

  /**
   * Main encode function
   */
  async encode() {
    try {
      

      this.onProgress(5);

      await this.initEncoder();
      this.onProgress(10);

      if (this.audioFile) {
        const audioOk = await this.initAudioEncoder();
        this.onProgress(15);
        if (audioOk) {
          await this.encodeAudio();
          this.onProgress(40);
        }
      }

      await this.generateVideoFrames();
      this.onProgress(95);

      this.muxer.finalize();
      const buffer = this.muxerTarget.buffer;
      
      

      const blob = new Blob([buffer], { type: 'video/webm' });
      const url = URL.createObjectURL(blob);
      const timestamp = new Date().toISOString().slice(0, 19).replace(/[:.]/g, '-');
      const filename = `video-hd-1080p-${timestamp}.webm`;

      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      setTimeout(() => URL.revokeObjectURL(url), 100);

      this.onProgress(100);
    

      return {
        success: true,
        filename,
        fileSize: buffer.byteLength,
        fileSizeMB: (buffer.byteLength / 1024 / 1024).toFixed(2),
        slideCount: this.slides.length,
        frameCount: this.frameCount,
        hasAudio: !!this.audioFile,
      };

    } catch (error) {
      
      throw error;
    } finally {
      if (this.encoder && this.encoder.state !== 'closed') {
        this.encoder.close();
      }
      if (this.audioEncoder && this.audioEncoder.state !== 'closed') {
        this.audioEncoder.close();
      }
    }
  }
}

export default WebCodecsVideoEncoder;